import React, { useState, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Divider,
  CircularProgress,
  Paper,
} from '@mui/material';
import { locationService, BulkCreateLocationDto } from '../services/locationService';
import { useTranslation } from 'react-i18next';

interface BulkCreateModalProps {
  open: boolean;
  warehouseId: string;
  parentId: string;
  parentCode: string;
  onClose: () => void;
  onCreated: () => void;
  showSnackbar: (msg: string, sev?: 'success' | 'error') => void;
}

const BulkCreateModal: React.FC<BulkCreateModalProps> = ({
  open,
  warehouseId,
  parentId,
  parentCode,
  onClose,
  onCreated,
  showSnackbar,
}) => {
  const { t } = useTranslation('warehouse');
  // Saha düzeni: bir peronda üst üste 5-6 raf, kat/bölme kullanılmıyor.
  const [rackCount, setRackCount] = useState(5);
  const [levelCount, setLevelCount] = useState(1);
  const [binCount, setBinCount] = useState(1);
  const [startNumber, setStartNumber] = useState(1);
  const [prefix, setPrefix] = useState('');
  const [loading, setLoading] = useState(false);

  const effectivePrefix = prefix || parentCode;

  const preview = useMemo(() => {
    const lines: string[] = [];
    const total = rackCount * Math.max(levelCount, 1) * Math.max(binCount, 1);
    const maxPreview = 20;

    for (let r = 0; r < rackCount && lines.length < maxPreview; r++) {
      const rCode = `${effectivePrefix}-R${String(startNumber + r).padStart(2, '0')}`;
      if (levelCount <= 1 && binCount <= 1) {
        lines.push(rCode);
      } else {
        for (let l = 1; l <= levelCount && lines.length < maxPreview; l++) {
          const lCode = `${rCode}-L${String(l).padStart(2, '0')}`;
          if (binCount <= 1) {
            lines.push(lCode);
          } else {
            const bins = [];
            for (let b = 1; b <= binCount; b++) {
              bins.push(`B${String(b).padStart(2, '0')}`);
            }
            lines.push(`${lCode} > ${bins.join(', ')}`);
          }
        }
      }
    }

    return { lines, total, truncated: lines.length >= maxPreview && total > maxPreview };
  }, [rackCount, levelCount, binCount, startNumber, effectivePrefix]);

  const handleCreate = async () => {
    try {
      setLoading(true);
      const dto: BulkCreateLocationDto = {
        WarehouseId: warehouseId,
        ParentId: parentId,
        RackCount: rackCount,
        LevelCount: levelCount,
        BinCount: binCount,
        StartNumber: startNumber,
        Prefix: prefix || undefined,
      };
      const response = await locationService.bulkCreate(dto);
      const count = response.data?.CreatedCount || response.data?.createdCount || preview.total;
      showSnackbar(t('components.bulkCreateModal.createdMessage', { count }));
      onCreated();
    } catch {
      showSnackbar(t('components.bulkCreateModal.createFailed'), 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('components.bulkCreateModal.title')}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('components.bulkCreateModal.target', { code: parentCode })}
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label={t('components.bulkCreateModal.rackCount')}
            type="number"
            value={rackCount}
            onChange={e => setRackCount(Math.max(1, parseInt(e.target.value) || 1))}
            size="small"
            fullWidth
            inputProps={{ min: 1, max: 100 }}
          />
          <TextField
            label={t('components.bulkCreateModal.levelCount')}
            type="number"
            value={levelCount}
            onChange={e => setLevelCount(Math.max(1, parseInt(e.target.value) || 1))}
            size="small"
            fullWidth
            inputProps={{ min: 1, max: 50 }}
          />
          <TextField
            label={t('components.bulkCreateModal.binCount')}
            type="number"
            value={binCount}
            onChange={e => setBinCount(Math.max(1, parseInt(e.target.value) || 1))}
            size="small"
            fullWidth
            inputProps={{ min: 1, max: 50 }}
          />
          <TextField
            label={t('components.bulkCreateModal.startNumber')}
            type="number"
            value={startNumber}
            onChange={e => setStartNumber(Math.max(1, parseInt(e.target.value) || 1))}
            size="small"
            fullWidth
            inputProps={{ min: 1 }}
          />
          <TextField
            label={t('components.bulkCreateModal.prefixLabel')}
            value={prefix}
            onChange={e => setPrefix(e.target.value)}
            size="small"
            fullWidth
            placeholder={parentCode}
          />
        </Box>

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle2" gutterBottom>
          {t('components.bulkCreateModal.previewTitle', { count: preview.total })}
        </Typography>
        <Paper variant="outlined" sx={{ p: 1.5, maxHeight: 200, overflow: 'auto', bgcolor: 'grey.50' }}>
          {preview.lines.map((line, i) => (
            <Typography key={i} variant="caption" display="block" sx={{ fontFamily: 'monospace', lineHeight: 1.6 }}>
              {line}
            </Typography>
          ))}
          {preview.truncated && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
              {t('components.bulkCreateModal.previewMore', { count: preview.total - preview.lines.length })}
            </Typography>
          )}
        </Paper>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>{t('common.cancel')}</Button>
        <Button
          variant="contained"
          onClick={handleCreate}
          disabled={loading || rackCount < 1}
          startIcon={loading ? <CircularProgress size={18} /> : undefined}
        >
          {loading ? t('components.bulkCreateModal.creating') : t('components.bulkCreateModal.createAction', { count: preview.total })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BulkCreateModal;
